import { createRoot } from "react-dom/client";
import { App } from "components/App";
import { SiteNameProvider } from "context/SiteName.context";
import { configureRecaptcha, createRecaptchaScriptElement } from "utils/recaptcha";
import { APP_FONTS_REGISTER_STYLESHEET_HREF, APP_FONTS_STYLESHEET_HREF, APP_STYLESHEET_HREF } from "utils/url.utils";
import "assets/styles/index.scss";
import "styles/playground.scss";

const main = () => {
  const appRootNode: HTMLElement | null = document.getElementById("auth_root");
  if (!appRootNode) return;

  const recaptchaSiteKey = appRootNode.dataset.recaptchaSiteKey ?? "";
  configureRecaptcha(recaptchaSiteKey);

  const siteName = appRootNode.dataset.siteName;
  if (!siteName) {
    throw new Error("Site name is not set");
  }

  const appContainer = document.createElement("div");
  appContainer.setAttribute("data-version", __APP_VERSION__);
  const appRoot = document.createElement("div");

  const appStylesheetNode = document.createElement("link");
  appStylesheetNode.type = "text/css";
  appStylesheetNode.rel = "stylesheet";
  appStylesheetNode.href = APP_STYLESHEET_HREF;

  const fontsRegisterStylesheetNode = document.createElement("link");
  fontsRegisterStylesheetNode.type = "text/css";
  fontsRegisterStylesheetNode.rel = "stylesheet";
  fontsRegisterStylesheetNode.href = APP_FONTS_REGISTER_STYLESHEET_HREF;

  const fontsStylesheetNode = document.createElement("link");
  fontsStylesheetNode.type = "text/css";
  fontsStylesheetNode.rel = "stylesheet";
  fontsStylesheetNode.href = APP_FONTS_STYLESHEET_HREF;

  // Assets are only awaited so they are *settled*: a failure on either must not
  // leave the app unrendered. Listeners are attached before the nodes are inserted,
  // because an already-cached asset can otherwise settle before we start listening
  // and its load event would then never be observed.
  let appRendered = false;
  let stylesheetSettled = false;
  let recaptchaSettled = !recaptchaSiteKey;

  const renderApp = () => {
    createRoot(appRoot).render(
      <SiteNameProvider value={{ siteName }}>
        <App />
      </SiteNameProvider>
    );
  };

  const tryRender = () => {
    if (appRendered || !stylesheetSettled || !recaptchaSettled) return;
    appRendered = true;
    renderApp();
  };

  const settleStylesheet = () => {
    stylesheetSettled = true;
    tryRender();
  };

  const settleRecaptcha = () => {
    recaptchaSettled = true;
    tryRender();
  };

  if (!__DEV_SERVER_LAUNCHED__) {
    appStylesheetNode.addEventListener("load", settleStylesheet);
    appStylesheetNode.addEventListener("error", settleStylesheet);
  }

  let recaptchaScript: HTMLScriptElement | null = null;
  if (recaptchaSiteKey) {
    recaptchaScript = createRecaptchaScriptElement(recaptchaSiteKey);
    recaptchaScript.addEventListener("load", settleRecaptcha);
    recaptchaScript.addEventListener("error", settleRecaptcha);
  }

  appRootNode.appendChild(fontsRegisterStylesheetNode);
  appContainer.appendChild(fontsStylesheetNode);
  appContainer.appendChild(appRoot);

  if (!__DEV_SERVER_LAUNCHED__) {
    appContainer.appendChild(appStylesheetNode);
  }

  appRootNode.appendChild(appContainer);

  if (recaptchaScript) {
    appRootNode.appendChild(recaptchaScript);
  }

  if (__DEV_SERVER_LAUNCHED__) {
    stylesheetSettled = true;
    recaptchaSettled = true;
  }

  tryRender();
};

if (__DEV_SERVER_LAUNCHED__) {
  main();
} else {
  (function checkForInit() {
    if (document.readyState === "complete") {
      return main();
    }
    window.requestAnimationFrame(() => {
      checkForInit();
    });
  })();
}
